import { useCallback, useState } from "react";
import { createViewBooks } from "@/modules/rev_apac/partners/views/ViewBooks";
import { NOOP } from "@/utils/utils";
import type { TCompositionDeps } from "@/ui/compositionRoot/types";

const PAGE_SIZE = 50;

export const wireSettingsBooks = ({ store: _store, useAppStore, api }: TCompositionDeps) => {
    const ViewBooks = createViewBooks({
        useCases: () => {
            const booksState = useAppStore((state) => state.booksState);
            const userPermissions = useAppStore((state) => state.user?.permissions ?? []);
            const setBooksLoading = useAppStore((state) => state.setBooksLoading);
            const setBooks = useAppStore((state) => state.setBooks);
            const setBooksError = useAppStore((state) => state.setBooksError);
            const [currentPage, setCurrentPage] = useState(1);
            const [total, setTotal] = useState(0);

            const loadBooks = useCallback(
                async (page: number) => {
                    setBooksLoading();
                    try {
                        const data = await api.books.list({
                            active_only: false,
                            skip: (page - 1) * PAGE_SIZE,
                            limit: PAGE_SIZE,
                        });
                        setTotal(data.total);
                        setBooks(
                            data.items.map((item) => ({
                                id: item.id,
                                companyId: item.company_id,
                                number: item.number,
                                name: item.name,
                                isBudgetBook: item.is_budget_book,
                                isActive: item.is_active,
                            })),
                        );
                    } catch (error) {
                        setBooksError(
                            error instanceof Error ? error : new Error("Failed to load books"),
                        );
                    }
                },
                [setBooksLoading, setBooks, setBooksError],
            );

            const onPageChange = useCallback(
                (page: number) => {
                    setCurrentPage(page);
                    void loadBooks(page);
                },
                [loadBooks],
            );

            return {
                onMount: useCallback(() => {
                    void loadBooks(1);
                }, [loadBooks]),
                onUnmount: NOOP,
                useBooksState: () => booksState,
                onRefresh: useCallback(() => {
                    void loadBooks(currentPage);
                }, [loadBooks, currentPage]),
                onCreateBook: useCallback(
                    async (data: { number: number; name: string; is_active: boolean }) => {
                        await api.books.create(data);
                        await loadBooks(currentPage);
                    },
                    [loadBooks, currentPage],
                ),
                onUpdateBook: useCallback(
                    async (
                        id: string,
                        data: { number?: number; name?: string; is_active?: boolean },
                    ) => {
                        await api.books.update(id, data);
                        await loadBooks(currentPage);
                    },
                    [loadBooks, currentPage],
                ),
                onDeleteBook: useCallback(
                    async (id: string) => {
                        await api.books.delete(id);
                        await loadBooks(currentPage);
                    },
                    [loadBooks, currentPage],
                ),
                onPageChange,
                currentPage: () => currentPage,
                pageSize: PAGE_SIZE,
                total: () => total,
                permissions: () => userPermissions,
            };
        },
    });

    return { ViewBooks };
};
